import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Megaphone,
  Shield,
  FileText,
  Settings,
  ScrollText,
  BarChart3,
  Mail,
  Send,
  LogOut,
  Menu,
  X
} from 'lucide-react'
import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'

interface AdminLayoutProps {
  children: React.ReactNode
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, userRoles, hasPermission, isSuperAdmin, isAdmin, isModerator, isEditor } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  // Definir elementos de navegación según roles y permisos
  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard,
      show: true
    },
    {
      name: 'Gestión de Usuarios',
      href: '/admin/usuarios',
      icon: Users,
      show: hasPermission('manage_users')
    },
    {
      name: 'Gestión de Cursos',
      href: '/admin/cursos',
      icon: BookOpen,
      show: hasPermission('manage_courses') || hasPermission('create_course')
    },
    {
      name: 'Gestión de Campañas',
      href: '/admin/campanas',
      icon: Megaphone,
      show: hasPermission('manage_campaigns')
    },
    {
      name: 'Moderación',
      href: '/admin/moderacion',
      icon: Shield,
      show: hasPermission('moderate_forum')
    },
    {
      name: 'Mensajes de Contacto',
      href: '/admin/contacto',
      icon: Mail,
      show: hasPermission('manage_users')
    },
    {
      name: 'Newsletter',
      href: '/admin/newsletter',
      icon: Send,
      show: hasPermission('manage_users')
    },
    {
      name: 'Logs de Auditoría',
      href: '/admin/logs',
      icon: ScrollText,
      show: hasPermission('access_logs')
    },
    {
      name: 'Documentos Legales',
      href: '/admin/legales',
      icon: FileText,
      show: hasPermission('manage_legal_docs')
    },
    {
      name: 'Configuración',
      href: '/admin/configuracion',
      icon: Settings,
      show: hasPermission('configure_system')
    }
  ].filter(item => item.show)

  // Obtener el rol principal para mostrar
  const primaryRole = userRoles[0]?.role_name || 'usuario'
  const roleLabels: Record<string, string> = {
    superadmin: 'Superadministrador',
    admin: 'Administrador',
    moderator: 'Moderador',
    editor: 'Editor'
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar para móvil */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
            <SidebarContent
              user={user}
              primaryRole={primaryRole}
              roleLabels={roleLabels}
              navigationItems={navigationItems}
              location={location}
              onClose={() => setSidebarOpen(false)}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Sidebar para desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="w-64 bg-white border-r border-gray-200">
          <SidebarContent
            user={user}
            primaryRole={primaryRole}
            roleLabels={roleLabels}
            navigationItems={navigationItems}
            location={location}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-auto">
        {/* Header móvil */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold text-education">Panel Admin</h1>
          <div className="w-10" />
        </div>

        {/* Contenido */}
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  )
}

interface SidebarContentProps {
  user: any
  primaryRole: string
  roleLabels: Record<string, string>
  navigationItems: any[]
  location: any
  onClose?: () => void
  onLogout: () => void
}

function SidebarContent({
  user,
  primaryRole,
  roleLabels,
  navigationItems,
  location,
  onClose,
  onLogout
}: SidebarContentProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-education">
            Panel Administrativo
          </h1>
          {onClose && (
            <button onClick={onClose} className="lg:hidden p-1 hover:bg-gray-100 rounded">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full bg-education text-white flex items-center justify-center font-bold">
            {user?.email?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.email}
            </p>
            <p className="text-xs text-gray-500">
              {roleLabels[primaryRole] || primaryRole}
            </p>
          </div>
        </div>
      </div>

      {/* Navegación */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.href
          const Icon = item.icon

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive
                  ? 'bg-education text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-white' : 'text-gray-500'}`} />
              {item.name}
            </Link>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-gray-200">
        <Link
          to="/dashboard"
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 mb-2"
        >
          <LayoutDashboard className="h-5 w-5 mr-3 text-gray-500" />
          Volver al Dashboard
        </Link>
        <button
          onClick={onLogout}
          className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50"
        >
          <LogOut className="h-5 w-5 mr-3" />
          Cerrar Sesión
        </button>
      </div>
    </div>
  )
}
